const useCasesData = [
  {
    slug: "azure-migration-danish-sme",
    title: "Moving 400+ Servers to Azure for a Danish Utility Leader",
    category: "Azure Migration",
    serviceLink: "/services/azure-cloud",
    summary:
      "A large Danish utility and waste organisation moved over 400 on-premise servers to Azure, including business-critical applications, SQL workloads, and operational systems previously running from local server rooms.",
    challenge:
      "The organisation operated one of the largest on-premise server estates in the Danish forsyning sector: over 400 physical and virtual servers spread across basement server rooms, running everything from ERP and SQL databases to operational control systems and customer-facing applications. Hardware was aging, patching was manual, disaster recovery was theoretical, and the cost of keeping the lights on was growing every year. Leadership wanted to get out of the data centre business entirely — but a migration at this scale, in a regulated utility environment, carries real operational risk. Getting the sequence, the governance and the landing zone right mattered before a single server moved.",
    solution:
      "We designed and deployed a full Azure Landing Zone as the foundation — hub-spoke networking, Azure Firewall, Entra ID as the single identity plane, and Azure Policy enforcing compliance from day one. All infrastructure was written as Bicep and deployed through Azure DevOps pipelines. We then migrated the 400+ server workloads in structured waves: assessment and dependency mapping first, lift-and-shift for straightforward workloads, and re-platforming for SQL databases onto Azure SQL Managed Instance where it made sense. Critical operational systems were migrated with parallel-run periods to eliminate risk. Azure Monitor and Microsoft Sentinel were built into the platform, not added afterwards.",
    outcome:
      "The organisation moved its server estate into Azure and removed the dependency on local server rooms. Over 400 workloads now run with unified identity, automated patching, built-in disaster recovery, and a full audit trail. The IT team shifted from maintaining hardware to governing a platform. Infrastructure costs became predictable, and the organisation gained the capacity to scale without buying more metal.",
    metrics: [
      { label: "Servers migrated", value: "400+" },
      { label: "Local server rooms", value: "Removed" },
      { label: "Sector position", value: "Early mover" },
      { label: "Identity systems", value: "One (Entra ID)" },
    ],
    techStack: [
      "Azure Landing Zones",
      "Entra ID",
      "Conditional Access",
      "Azure Policy",
      "Bicep IaC",
      "Azure DevOps",
      "Azure Migrate",
      "Azure SQL Managed Instance",
      "Azure Firewall",
      "Azure Update Manager",
      "Microsoft Sentinel",
      "Azure Monitor",
    ],
    tags: ["Migration", "Governance", "IaC", "Azure"],
  },
  {
    slug: "ai-guardrails-financial-services",
    title: "AI Guardrails for a Financial Services Firm",
    category: "AI Governance",
    serviceLink: "/services/ai-governance",
    summary:
      "Deployed Microsoft Copilot and Azure OpenAI across three business units with no recorded data incidents in the measured period — governed from day one through Purview, Content Safety, and Responsible AI logging.",
    challenge:
      "The board wanted AI tools rolled out to 200 staff, but the compliance team blocked it. Sensitive client data could leak into AI models. Employees would use consumer ChatGPT anyway. There was no framework to audit what questions were asked, what answers were given, or which data the AI touched.",
    solution:
      "We built an AI governance framework on top of Microsoft's own tooling. Microsoft Purview classified and labelled all sensitive data. Azure AI Content Safety screened inputs and outputs. Azure AI Foundry served all AI interactions through a governed endpoint with logging enabled. Entra ID Conditional Access blocked direct access to consumer AI services from corporate devices. Every AI interaction was logged and searchable in Microsoft Sentinel.",
    outcome:
      "AI deployed across Finance, Legal, and Operations. No recorded data incidents in the first six months. Full audit trail for every AI interaction. Compliance signed off within 3 weeks of go-live.",
    metrics: [
      { label: "Business units live", value: "3" },
      { label: "Recorded data incidents", value: "0" },
      { label: "Compliance sign-off", value: "3 weeks" },
      { label: "AI interactions logged", value: "100%" },
    ],
    techStack: [
      "Microsoft Purview",
      "Azure AI Content Safety",
      "Azure AI Foundry",
      "Entra ID",
      "Conditional Access",
      "Microsoft Sentinel",
      "Azure OpenAI",
      "Microsoft Copilot",
    ],
    tags: ["AI", "Governance", "Compliance", "Security"],
  },
  {
    slug: "cicd-transformation-azure-devops",
    title: "CI/CD Transformation with Azure DevOps",
    category: "Software Development",
    serviceLink: "/services/software-development",
    summary:
      "Replaced FTP deployments and publish profiles with fully automated Azure DevOps pipelines — release cycle went from 3 weeks to 2 days, with no deployment-caused production incidents in the measured period.",
    challenge:
      "Eight developers were deploying directly to production via FTP and Visual Studio publish profiles. There was no branching strategy, no code review process, and no way to roll back. Production broke twice in a single month. Releases were feared events that happened on Friday evenings because 'at least the weekend absorbs the fallout'.",
    solution:
      "We introduced trunk-based development with short-lived feature branches and mandatory pull request reviews. Azure DevOps pipelines built, tested, and deployed every merge to main automatically. Infrastructure was moved to Bicep and deployed through the same pipeline as application code. Environment promotion (dev → staging → production) required explicit approval gates. Every deployment was tagged, versioned, and rollback-ready in under 2 minutes.",
    outcome:
      "Friday evening deployments became Tuesday afternoon deployments — by choice, not necessity. The team shipped 3× more often, with no deployment-caused production incidents in the 6 months following go-live.",
    metrics: [
      { label: "Release cycle", value: "2 days" },
      { label: "Deployment frequency", value: "3×" },
      { label: "Deployment-caused incidents", value: "0" },
      { label: "Rollback time", value: "< 2 min" },
    ],
    techStack: [
      "Azure DevOps",
      "Azure Pipelines",
      "GitHub",
      "Bicep IaC",
      "Azure App Service",
      "Azure Container Apps",
      "SonarQube",
      "OWASP Dependency Check",
    ],
    tags: ["CI/CD", "DevOps", "Automation", "IaC"],
  },
  {
      slug: "power-platform-operations-automation",
      title: "Access Package Governance for Production Access",
      category: "Azure Governance",
      serviceLink: "/services/azure-cloud",
      summary:
        "Delivered a full access management system for a customer so shared mailboxes, groups, and production access are all requested, approved, audited, and automatically removed when policy conditions change.",
      challenge:
        "The customer needed a proper access management model for production-related access, shared mailboxes, and group memberships. Too much responsibility sat with IT supporters, while the departments that actually owned the access decisions had limited control and limited accountability. When employees changed afdeling or left the company, access could remain longer than it should. The organisation needed audited access, expiry controls, ownership in the business, and automatic cleanup based on company policy.",
      solution:
        "We implemented a full Entra ID Governance model built around Access Packages and policy-driven assignment rules. Departments became responsible for approving and owning access for their own users instead of pushing every decision through central IT. Shared mailboxes, groups, and production-related permissions were wrapped in governed packages with approval flows, expiry settings, access reviews, and notification rules. Policies were configured around the customer's own operating model, including automatic removal when a user no longer worked at the company or moved to another afdeling. Every assignment became auditable, every expiry generated notifications, and access ownership moved closer to the people who understand the business risk.",
      outcome:
        "The customer gained a repeatable and compliant access management system instead of manual ticket-based administration. Departments now manage their own user access within policy boundaries, every production-related permission is auditable, and stale access is removed automatically when employment or departmental conditions change. That reduced operational overhead for IT support while strengthening compliance and governance at the same time.",
      metrics: [
        { label: "Access model", value: "Policy-based" },
        { label: "Auditability", value: "Full" },
        { label: "Ownership", value: "Department-led" },
        { label: "Access cleanup", value: "Automatic" },
      ],
      techStack: [
        "Microsoft Entra ID Governance",
        "Access Packages",
        "Entitlement Management",
        "Access Reviews",
        "Shared Mailboxes",
        "Microsoft 365 Groups",
        "Conditional Access",
        "Microsoft Graph API",
        "Azure RBAC",
        "Audit Logs",
      ],
      tags: ["Governance", "Entra ID", "Security", "Compliance"],
  },
  {
    slug: "entra-id-rollout-200-person-org",
    title: "Entra ID Rollout Across a 200-Person Organisation",
    category: "Azure Governance",
    serviceLink: "/services/azure-cloud",
    summary:
      "Consolidated six identity systems into one, enforced MFA across all 200 users, and cut offboarding time from 2 days to 15 minutes.",
    challenge:
      "The company had grown through acquisitions. Six identity systems existed in parallel. Three SaaS applications used shared passwords stored in a spreadsheet. New starters sometimes waited a week for access. When staff left, accounts were never consistently removed — two former employees still had active access six months after leaving.",
    solution:
      "We made Entra ID the single identity plane for the entire organisation. All 200 users migrated in two waves without planned user-facing downtime. 14 SaaS applications were integrated via SAML or OIDC — employees signed in once and had access to everything. Conditional Access policies enforced MFA, device compliance, and location-based restrictions. Automated lifecycle management provisioned new starters within 15 minutes of their HR record being created, and deprovisioned leavers the moment they were marked as departed.",
    outcome:
      "Six identity systems became one. MFA enforced for all 200 users on day one. Offboarding reduced from 2 days to 15 minutes. IT helpdesk password reset tickets dropped by 70%.",
    metrics: [
      { label: "Identity systems", value: "6 → 1" },
      { label: "MFA adoption", value: "100%" },
      { label: "Offboarding time", value: "15 min" },
      { label: "Helpdesk tickets", value: "−70%" },
    ],
    techStack: [
      "Entra ID",
      "Conditional Access",
      "SAML / OIDC",
      "SSPR",
      "Microsoft Graph API",
      "Azure AD Connect",
      "Lifecycle Workflows",
      "Microsoft Intune",
    ],
    tags: ["Identity", "Security", "Entra ID", "Governance"],
  },
  {
    slug: "event-driven-utility-platform",
    title: "Building a Secure Event-Driven Platform for Utility Operations",
    category: "Microsoft Azure",
    serviceLink: "/services/azure",
    summary:
      "Designed a coherent, credential-free Azure platform for a utility operator — web services, daemons, event ingestion, databases, storage and externally exposed APIs — with observability and security built in from the start.",
    challenge:
      "Operational applications needed to process events, run background workloads, expose APIs and securely access data and secrets. The platform spanned web services, daemon processes, event ingestion, databases, storage and externally exposed APIs. Without a deliberate architecture it would inevitably become a tangle of point-to-point integrations, hard-coded credentials and permissions granted out of convenience. Security and monitoring would be bolted on after the fact — or not at all.",
    solution:
      "We designed the platform around managed Azure services with a clear separation of concerns: .NET services hosted on Azure App Service for synchronous workloads, Event Hubs for asynchronous event ingestion and inter-service communication, Azure SQL Database for application data, Blob Storage for files and objects, and Key Vault for all secrets and certificates. API Management sat in front of all externally exposed APIs with rate limiting, authentication and policy enforcement. Application Gateway handled ingress. Application Insights was wired into every service from day one — not retrofitted. Critically, every service-to-service call used Managed Identities with narrowly scoped Azure RBAC assignments. No connection strings in config files, no shared secrets, no overly permissive roles.",
    outcome:
      "The solution established a reusable platform pattern that the organisation can apply to future operational applications. Services communicate without embedding credentials. Operational components are cleanly separated. Monitoring is part of the platform architecture, not an afterthought. The pattern is documented and repeatable — onboarding a new service means following the pattern, not making new decisions from scratch.",
    metrics: [
      { label: "Service authentication", value: "No embedded creds" },
      { label: "Architecture style", value: "Event-driven" },
      { label: "API exposure", value: "Centralised" },
      { label: "Observability", value: "Built-in" },
    ],
    techStack: [
      "Azure App Service",
      ".NET 9",
      "Azure Event Hubs",
      "Azure SQL Database",
      "Blob Storage",
      "Key Vault",
      "API Management",
      "Application Gateway",
      "Application Insights",
      "Managed Identity",
      "Azure RBAC",
    ],
    tags: ["Azure", "Architecture", "Security", "Event-driven", "Observability"],
  },
  {
    slug: "oracle-azure-latency-reduction",
    title: "Solving Cross-Platform Latency Between Azure and Oracle Exascale",
    category: "Microsoft Azure",
    serviceLink: "/services/azure",
    summary:
      "Reduced TCP connection latency to an Oracle Database@Azure Exascale listener to ~1 ms average by aligning compute, Oracle infrastructure and advanced networking across three infrastructure layers — and turned the solution into a repeatable IaC deployment model.",
    challenge:
      "A business-critical insurance platform was being modernised on Azure while its database ran on Oracle Database@Azure Exascale. Although the application and database were deployed in the same Azure region and virtual network, repeated database calls introduced enough latency to significantly affect API response times. The challenge was not ordinary network troubleshooting. The physical placement of Azure compute, Oracle infrastructure and advanced networking components all had to be considered together — and that required working at a level below what standard Azure tooling exposes.",
    solution:
      "We isolated the latency across the application, database and network layers and worked directly with Microsoft engineering teams on physical infrastructure alignment. The solution included aligning Azure compute and Oracle infrastructure within the same availability zone, coordinating Advanced Networking pinning, and using Proximity Placement Groups to control compute placement. We designed the entire deployment sequence around physical capacity constraints — order of resource creation matters when you are pinning to specific hardware. All Azure infrastructure was automated through Bicep so the placement decisions were codified rather than tribal knowledge. We also documented the trade-off between low latency, zonal resilience and deployment flexibility so the organisation could make an informed choice as the platform grows.",
    outcome:
      "TCP connection latency to the Oracle listener was reduced to approximately 1 millisecond on average from the Azure workload. More importantly, the organisation gained a repeatable, IaC-based deployment model for production rather than relying on ad hoc placement and trial-and-error troubleshooting. Any future environment — staging, DR, new region — can be spun up with the same placement guarantees from day one.",
    metrics: [
      { label: "Avg Oracle listener latency", value: "~1 ms" },
      { label: "Infrastructure layers aligned", value: "3" },
      { label: "Deployment model", value: "IaC" },
      { label: "Placement strategy", value: "Prod-ready" },
    ],
    techStack: [
      "Oracle Database@Azure",
      "Oracle Exascale",
      "Azure Virtual Machines",
      "Proximity Placement Groups",
      "Availability Zones",
      "Advanced Networking",
      "Bicep",
      "Azure Monitor",
    ],
    tags: ["Azure", "Oracle", "Networking", "IaC", "Performance"],
  },
  {
    slug: "api-centralisation-azure-apim",
    title: "Centralising Scattered APIs Under One Secure Gateway",
    category: "Azure Governance",
    serviceLink: "/services/azure-cloud",
    summary:
      "A client running APIs across a mix of on-premise systems and cloud applications had no single point of control, no consistent authentication, and growing security exposure. We consolidated everything under Azure API Management — one gateway, one policy engine, one front door — backed by Application Gateway and WAF to protect every inbound call.",
    challenge:
      "The client had grown through acquisitions and organic development. APIs were scattered: some hosted on-premise on IIS, some in Azure App Services, some in third-party SaaS platforms. Each consumed by internal applications in different ways, with no consistent authentication enforcement, no centralised logging, and no way to know which system was calling which API. Security reviews flagged this as a critical exposure: any application, internal or external, could call any backend directly. There was no layer to enforce who was allowed to call what, and the attack surface was growing as more services were added.",
    solution:
      "We placed Azure API Management (APIM) as the single gateway in front of all APIs — both on-premise and cloud-hosted. Every consumer, whether internal application or external partner, now talks exclusively to APIM. Backends are never called directly.\n\nAPIM policies enforce authentication before any call reaches a backend: OAuth 2.0 via Entra ID, strict JWT validation on all inbound tokens, and application-level authorization policies mapped to each API product. Rate limiting, IP filtering, and request transformation are all handled at the gateway — keeping backends clean.\n\nFor the inbound path from the internet, we placed Azure Application Gateway in front of APIM with Web Application Firewall (WAF) enabled. WAF is a managed rule set that inspects every HTTP request before it enters the platform — blocking SQL injection, cross-site scripting, protocol anomalies, and other OWASP Top 10 attack patterns before they reach application code. Backends sit in a private subnet and are unreachable from outside the platform.",
    outcome:
      "All APIs — on-premise and cloud — are now under one roof. The security team has a single dashboard showing every API call, who made it, and whether it was allowed or denied. Authentication is no longer the responsibility of individual backend developers. WAF blocks hundreds of malicious probe requests weekly that previously reached application code directly. The client can onboard a new API in hours, with policies and authentication inherited automatically.",
    metrics: [
      { label: "APIs centralised", value: "All" },
      { label: "Direct backend exposure", value: "None" },
      { label: "Auth enforcement", value: "Gateway-level" },
      { label: "WAF rules active", value: "OWASP Top 10" },
    ],
    techStack: [
      "Azure API Management",
      "Azure Application Gateway",
      "Web Application Firewall (WAF)",
      "Entra ID",
      "OAuth 2.0 / JWT",
      "Azure Virtual Network",
      "Private Endpoints",
      "Azure Monitor",
      "Bicep IaC",
      "Azure DevOps",
    ],
    tags: ["API", "Security", "Networking", "Governance", "Azure"],
  },
];

export default useCasesData;
